import { useState, useMemo, useCallback } from 'react';
import { Plus, FileText, Repeat, Loader2 } from 'lucide-react';
import MonthCalendar from '../components/meetings/MonthCalendar';
import DayMeetingsList from '../components/meetings/DayMeetingsList';
import MeetingDetailView from '../components/meetings/MeetingDetailView';
import MeetingModeView from '../components/meetings/MeetingModeView';
import MeetingModal from '../components/meetings/MeetingModal';
import MeetingTemplateModal from '../components/meetings/MeetingTemplateModal';
import MeetingSeriesModal from '../components/meetings/MeetingSeriesModal';
import ConfirmModal from '../components/ConfirmModal';
import { useMeetings } from '../hooks/useMeetings';
import { useDocuments } from '../hooks/useDocuments';
import { usePolicies } from '../hooks/usePolicies';
import { getLocalDateString } from '../utils/dateUtils';
import FeatureOnboarding from '../components/common/FeatureOnboarding';

export default function MeetingsPage() {
  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(getLocalDateString(new Date()));
  const [selectedMeeting, setSelectedMeeting] = useState(null);

  // Modal state
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showSeriesModal, setShowSeriesModal] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editingSeries, setEditingSeries] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, type: null, item: null });

  // Dropdown state
  const [showActionsMenu, setShowActionsMenu] = useState(false);

  // Meeting mode state
  const [meetingModeActive, setMeetingModeActive] = useState(false);

  // Meetings Hook
  const {
    meetings,
    templates,
    series,
    loading,
    error,
    addMeeting,
    updateMeeting,
    completeMeeting,
    cancelMeeting,
    deleteMeeting,
    addTemplate,
    updateTemplate,
    archiveTemplate,
    addSeries,
    updateSeries,
    archiveSeries,
    getMeetingsForDate,
    getMeetingCountsByDate,
    materializeSeriesInstance
  } = useMeetings();

  // Documents and Policies Hooks (for agenda item linking)
  const { documents } = useDocuments();
  const { policies } = usePolicies();

  // Get meetings for the selected date
  const dayMeetings = useMemo(() => {
    return getMeetingsForDate(selectedDate);
  }, [getMeetingsForDate, selectedDate]);

  // Get meeting counts for calendar display
  const meetingCounts = useMemo(() => {
    return getMeetingCountsByDate(currentMonth.getFullYear(), currentMonth.getMonth());
  }, [getMeetingCountsByDate, currentMonth]);

  // Handle meeting selection (materialize if virtual)
  const handleMeetingSelect = useCallback(async (meeting) => {
    console.log('[handleMeetingSelect] Meeting:', { id: meeting.id, isVirtual: meeting.isVirtual, isShared: meeting.isShared });
    if (meeting.isVirtual) {
      try {
        // Materialize the virtual instance when selected
        const materialized = await materializeSeriesInstance(meeting);
        console.log('[handleMeetingSelect] Materialized result:', { id: materialized.id, isVirtual: materialized.isVirtual });
        setSelectedMeeting(materialized);
      } catch (error) {
        console.error('[handleMeetingSelect] Error materializing:', error);
        // Still set the virtual meeting so user can see it
        setSelectedMeeting(meeting);
      }
    } else {
      setSelectedMeeting(meeting);
    }
  }, [materializeSeriesInstance]);

  // Handle add meeting
  const handleAddMeeting = () => {
    setEditingMeeting(null);
    setShowMeetingModal(true);
  };

  // Handle edit meeting
  const handleEditMeeting = (meeting) => {
    setEditingMeeting(meeting);
    setShowMeetingModal(true);
  };

  // Handle save meeting
  const handleSaveMeeting = async (data) => {
    try {
      if (editingMeeting) {
        await updateMeeting(editingMeeting.id, data);
        if (selectedMeeting?.id === editingMeeting.id) {
          setSelectedMeeting({ ...selectedMeeting, ...data });
        }
      } else {
        const docRef = await addMeeting({
          ...data,
          date: selectedDate
        });
        // Select the newly created meeting
        setSelectedMeeting({ id: docRef.id, ...data, date: selectedDate });
      }
      setShowMeetingModal(false);
      setEditingMeeting(null);
    } catch (err) {
      console.error('Error saving meeting:', err);
    }
  };

  // Handle meeting completion
  const handleCompleteMeeting = async (meeting) => {
    await completeMeeting(meeting.id);
    if (selectedMeeting?.id === meeting.id) {
      setSelectedMeeting({ ...selectedMeeting, status: 'completed' });
    }
  };

  // Handle meeting update (for notes, agenda items, etc.)
  const handleUpdateMeeting = async (id, data) => {
    await updateMeeting(id, data);
    if (selectedMeeting?.id === id) {
      setSelectedMeeting({ ...selectedMeeting, ...data });
    }
  };

  // Handle delete
  const handleDelete = async () => {
    const { type, item } = deleteConfirm;
    try {
      if (type === 'meeting') {
        await deleteMeeting(item.id);
        if (selectedMeeting?.id === item.id) {
          setSelectedMeeting(null);
        }
      } else if (type === 'template') {
        await archiveTemplate(item.id);
      } else if (type === 'series') {
        await archiveSeries(item.id);
      }
      setDeleteConfirm({ show: false, type: null, item: null });
    } catch (err) {
      console.error('Error deleting:', err);
    }
  };

  // Template handlers
  const handleAddTemplate = () => {
    setEditingTemplate(null);
    setShowTemplateModal(true);
    setShowActionsMenu(false);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setShowTemplateModal(true);
  };

  const handleSaveTemplate = async (data) => {
    try {
      if (editingTemplate) {
        await updateTemplate(editingTemplate.id, data);
      } else {
        await addTemplate(data);
      }
      setShowTemplateModal(false);
      setEditingTemplate(null);
    } catch (err) {
      console.error('Error saving template:', err);
    }
  };

  // Series handlers
  const handleAddSeries = () => {
    setEditingSeries(null);
    setShowSeriesModal(true);
    setShowActionsMenu(false);
  };

  const handleEditSeries = (seriesItem) => {
    setEditingSeries(seriesItem);
    setShowSeriesModal(true);
  };

  const handleSaveSeries = async (data) => {
    try {
      if (editingSeries) {
        await updateSeries(editingSeries.id, data);
      } else {
        await addSeries(data);
      }
      setShowSeriesModal(false);
      setEditingSeries(null);
    } catch (err) {
      console.error('Error saving series:', err);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 size={24} className="animate-spin text-accent" />
      </div>
    );
  }

  return (
    <FeatureOnboarding feature="meetings">
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-surface-base">
        <h1 className="text-lg font-semibold text-text-primary">Meetings</h1>
        <div className="flex items-center gap-2">
          <div className="relative">
            <button
              onClick={() => setShowActionsMenu(!showActionsMenu)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-text-secondary hover:text-text-primary hover:bg-surface-hover rounded-lg transition-colors"
            >
              <Plus size={14} />
              Create
            </button>
            {showActionsMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowActionsMenu(false)}
                />
                <div className="absolute right-0 mt-1 w-48 bg-surface-card border border-border rounded-lg shadow-lg z-20 py-1">
                  <button
                    onClick={handleAddTemplate}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-text-primary hover:bg-surface-hover transition-colors"
                  >
                    <FileText size={14} className="text-text-tertiary" />
                    New Template
                  </button>
                  <button
                    onClick={handleAddSeries}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-text-primary hover:bg-surface-hover transition-colors"
                  >
                    <Repeat size={14} className="text-text-tertiary" />
                    New Recurring Series
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex min-h-0">
        {/* Left panel: Calendar + Day list */}
        <div className="w-80 flex flex-col border-r border-border bg-surface-base">
          <div className="p-4">
            <MonthCalendar
              currentMonth={currentMonth}
              onMonthChange={setCurrentMonth}
              selectedDate={selectedDate}
              onDateSelect={setSelectedDate}
              meetingCounts={meetingCounts}
            />
          </div>
          <DayMeetingsList
            date={selectedDate}
            meetings={dayMeetings}
            selectedMeeting={selectedMeeting}
            onMeetingSelect={handleMeetingSelect}
            onAddMeeting={handleAddMeeting}
          />
        </div>

        {/* Right panel: Meeting detail */}
        <div className="flex-1 bg-surface-elevated">
          {selectedMeeting ? (
            <MeetingDetailView
              meeting={selectedMeeting}
              onClose={() => setSelectedMeeting(null)}
              onEdit={() => handleEditMeeting(selectedMeeting)}
              onComplete={() => handleCompleteMeeting(selectedMeeting)}
              onUpdate={(data) => handleUpdateMeeting(selectedMeeting.id, data)}
              onDelete={() => setDeleteConfirm({ show: true, type: 'meeting', item: selectedMeeting })}
              onEnterMeetingMode={() => setMeetingModeActive(true)}
              series={series.find(s => s.id === selectedMeeting.seriesId)}
              documents={documents}
              policies={policies}
            />
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center p-8">
              <div className="w-16 h-16 rounded-full bg-surface-hover flex items-center justify-center mb-4">
                <FileText size={24} className="text-text-muted" />
              </div>
              <h3 className="text-sm font-medium text-text-primary mb-1">
                No meeting selected
              </h3>
              <p className="text-xs text-text-tertiary max-w-[200px]">
                Select a meeting from the list or create a new one
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <MeetingModal
        isOpen={showMeetingModal}
        onClose={() => {
          setShowMeetingModal(false);
          setEditingMeeting(null);
        }}
        onSave={handleSaveMeeting}
        editItem={editingMeeting}
        templates={templates}
        series={series}
        defaultDate={selectedDate}
      />

      <MeetingTemplateModal
        isOpen={showTemplateModal}
        onClose={() => {
          setShowTemplateModal(false);
          setEditingTemplate(null);
        }}
        onSave={handleSaveTemplate}
        editItem={editingTemplate}
      />

      <MeetingSeriesModal
        isOpen={showSeriesModal}
        onClose={() => {
          setShowSeriesModal(false);
          setEditingSeries(null);
        }}
        onSave={handleSaveSeries}
        editItem={editingSeries}
        templates={templates}
      />

      <ConfirmModal
        isOpen={deleteConfirm.show}
        onClose={() => setDeleteConfirm({ show: false, type: null, item: null })}
        onConfirm={handleDelete}
        title={`Delete ${deleteConfirm.type || 'item'}?`}
        message={`Are you sure you want to delete this ${deleteConfirm.type || 'item'}? This action cannot be undone.`}
        confirmLabel="Delete"
        confirmVariant="danger"
      />

      {/* Meeting Mode Overlay */}
      {meetingModeActive && selectedMeeting && (
        <MeetingModeView
          meeting={selectedMeeting}
          onClose={() => setMeetingModeActive(false)}
          onUpdate={(data) => handleUpdateMeeting(selectedMeeting.id, data)}
          onComplete={() => {
            handleCompleteMeeting(selectedMeeting);
            setMeetingModeActive(false);
          }}
          documents={documents}
          policies={policies}
        />
      )}
    </div>
    </FeatureOnboarding>
  );
}
